import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ShieldCheck, Camera, FileCheck2, Truck, PoundSterling,
  Plus, Landmark, BadgeCheck,
} from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp, MaskedLine } from "@/components/Motion";

const FAQS = [
  {
    q: "Who actually holds my money?",
    a: "An independent, FCA-regulated payment service holds your payment in a safeguarded account. Solaris Transport cannot touch those funds until you confirm your vehicle has been delivered safely.",
  },
  {
    q: "When is my payment released?",
    a: "Only after your vehicle arrives and you have inspected it and confirmed you are happy. You stay in control of the release from start to finish.",
  },
  {
    q: "What happens if there is a problem?",
    a: "If anything is not right on delivery, the funds simply stay held by the independent payment service while the issue is resolved. You are never left chasing a refund.",
  },
  {
    q: "Do I pay extra for this protection?",
    a: "No. Payment protection is included as standard on every transport booking with Solaris Transport — it is simply how we believe vehicle transport should work.",
  },
  {
    q: "How do I pay?",
    a: "Once you accept your quote, the independent payment service sends you secure instructions to pay by bank transfer or card into your safeguarded account. We never ask for card details by email or over the phone.",
  },
];

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const PILLARS = [
  { icon: Landmark, title: "Independent FCA-regulated service", copy: "Funds are held by a payment institution authorised and supervised by the Financial Conduct Authority — completely separate from our own accounts." },
  { icon: Camera, title: "Verified collection", copy: "Every collection is documented with photographs and a signed condition report before your vehicle leaves its starting point." },
  { icon: FileCheck2, title: "Digital proof of delivery", copy: "You confirm safe delivery digitally. That confirmation is the trigger that releases your payment — nothing else." },
  { icon: Truck, title: "Goods in Transit cover", copy: "Your vehicle is covered while it is on our lorries, so it is protected physically just as your money is protected financially." },
];

const AccordionItem = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-display text-base font-semibold text-slate-900">{q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
          <Plus className="h-5 w-5 text-blue-700" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-sm leading-relaxed text-slate-600">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PaymentProtection = () => (
  <div data-testid="payment-protection-page">
    <Seo
      title="Payment Protection | Solaris Transport Ltd"
      description="Your transport payment is held by an independent, FCA-regulated payment service and only released when you confirm safe delivery. See how it works."
      path="/payment-protection"
      jsonLd={FAQ_JSONLD}
    />

    {/* Hero */}
    <section className="relative overflow-hidden border-b border-slate-200 bg-white" data-testid="pp-hero">
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl" />
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5"
            data-testid="pp-eyebrow"
          >
            <ShieldCheck className="h-4 w-4 text-emerald-700" />
            <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Payment Protection</span>
          </motion.div>
          <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="pp-headline">
            <MaskedLine delay={0.1}>Your money stays</MaskedLine>
            <MaskedLine delay={0.22}>
              <span className="text-blue-700">protected</span> until the
            </MaskedLine>
            <MaskedLine delay={0.34}>job is done.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
            data-testid="pp-subcopy"
          >
            Handing money to a transport company you've never met takes trust.
            So we removed the need for it: every payment is held by an
            independent, FCA-regulated payment service and released to us only
            when you confirm your vehicle has arrived safely.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-8 flex flex-wrap gap-3"
            data-testid="pp-trust-chips"
          >
            {["FCA-regulated partner", "Independent custody of funds", "Released on your confirmation"].map((c) => (
              <span key={c} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700">
                <BadgeCheck className="h-4 w-4 text-blue-700" /> {c}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>

    {/* 6-step infographic */}
    <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28" data-testid="pp-steps-section">
      <FadeUp>
        <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">The safeguarded payment process</p>
        <h2 className="mt-3 max-w-2xl font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
          Six steps between your £ and our lorry
        </h2>
      </FadeUp>

      <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="pp-steps-grid">
        {[
          { t: "Request your quote", c: "Tell us what you're moving and where. We send a fixed, customised quote in pounds — no hidden extras." },
          { t: "Pay into a safeguarded account", c: "Your £ goes to an independent, FCA-regulated payment service — never directly to Solaris Transport." },
          { t: "We collect your vehicle", c: "Collection is verified with photographs and a signed condition report before we set off." },
          { t: "Funds stay protected in transit", c: "While your vehicle is on our lorries, your £ stays safely held by the independent service." },
          { t: "You confirm delivery", c: "On arrival you inspect your vehicle and digitally confirm everything is exactly as it should be." },
          { t: "Payment is released", c: "Only then is the £ released to Solaris Transport. If something isn't right, the funds stay protected while it's put right." },
        ].map((s, i) => (
          <FadeUp key={s.t} delay={(i % 3) * 0.08}>
            <div className="relative h-full rounded-2xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-900/5" data-testid={`pp-step-card-${i}`}>
              <div className="flex items-center justify-between">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-700">
                  <PoundSterling className="h-6 w-6 text-white" />
                </span>
                <span className="font-mono text-4xl font-semibold text-blue-100">0{i + 1}</span>
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold tracking-tight text-slate-900">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.c}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </section>

    {/* Pillars */}
    <section className="border-y border-slate-200 bg-slate-50" data-testid="pp-pillars-section">
      <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
        <FadeUp>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Protection that goes beyond the payment
          </h2>
        </FadeUp>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="pp-pillars-grid">
          {PILLARS.map((p, i) => (
            <FadeUp key={p.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6" data-testid={`pp-pillar-${i}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50">
                  <p.icon className="h-5 w-5 text-blue-700" />
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.copy}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="mx-auto max-w-4xl px-5 py-20 lg:px-8 lg:py-28" data-testid="pp-faq-section">
      <FadeUp className="text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">Common questions</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
          Everything customers ask about payment protection
        </h2>
      </FadeUp>
      <div className="mt-10 space-y-3" data-testid="pp-faq-list">
        {FAQS.map((f, i) => (
          <FadeUp key={f.q} delay={i * 0.05}>
            <AccordionItem q={f.q} a={f.a} testid={`pp-faq-${i}`} />
          </FadeUp>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="mx-auto max-w-7xl px-5 pb-20 lg:px-8" data-testid="pp-cta-section">
      <FadeUp>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-800 via-blue-700 to-blue-900 p-8 sm:p-12">
          <PoundSterling className="pointer-events-none absolute -bottom-12 -right-8 h-56 w-56 text-white/5" strokeWidth={1} />
          <div className="relative flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                Book transport with built-in protection
              </h2>
              <p className="mt-3 max-w-xl text-base text-blue-100">
                Get a fixed quote today — payment protection is included on
                every single booking, at no extra cost.
              </p>
            </div>
            <Link to="/contact" data-testid="pp-cta-quote-button" className="group flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-bold text-blue-800 transition-colors duration-200 hover:bg-blue-50">
              Get a protected quote <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </FadeUp>
    </section>
  </div>
);

export default PaymentProtection;
